import React, { useState, useEffect } from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Typography,
  Box,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
  Select,
  MenuItem,
  FormControl,
  Collapse,
  ListItemIcon,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SourceIcon from '@mui/icons-material/Source';
import ArticleIcon from '@mui/icons-material/Article';
import SettingsIcon from '@mui/icons-material/Settings';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import FolderIcon from '@mui/icons-material/Folder';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import PeopleIcon from '@mui/icons-material/People';
import StreamIcon from '@mui/icons-material/Stream';
import CalendarIcon from '@mui/icons-material/CalendarMonth';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import ImageIcon from '@mui/icons-material/Image';
import { getProjects, createProject } from '../services/api';
import { useNavigate, useLocation } from 'react-router-dom';
import { useProject } from '../contexts/ProjectContext';

export const DRAWER_WIDTH = 240;

const MENU_ITEMS = [
  { id: 'dashboard', label: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
  { id: 'articles', label: 'Articles', icon: <ArticleIcon />, path: '/articles' },
  { id: 'media', label: 'Media', icon: <ImageIcon />, path: '/media' },
  { id: 'brandvoice-style', label: 'Brand Voice', icon: <RecordVoiceOverIcon />, path: '/brandvoice/style' },
  { id: 'resources', label: 'Resources', icon: <FolderIcon />, path: '/sources/resources' },
  { id: 'calendar', label: 'Calendar', icon: <CalendarIcon />, path: '/calendar' },
  { id: 'ideas', label: 'Ideas', icon: <LightbulbIcon />, path: '/ideas' },
  { id: 'settings', label: 'Settings', icon: <SettingsIcon />, path: '/settings' },
];

const ProjectSidebar = ({ mobileOpen = false, onMobileClose, showBackButton }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { 
    projects, 
    selectedProject, 
    setSelectedProject, 
    reloadProjects 
  } = useProject();
  
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectDescription, setNewProjectDescription] = useState('');
  const [openSubmenu, setOpenSubmenu] = useState('sources');

  const getCurrentMenuItem = () => {
    const [main, sub] = location.pathname.split('/').filter(Boolean);
    return sub || main || 'dashboard';
  };

  const handleMenuClick = (item) => {
    if (item.submenu) {
      setOpenSubmenu(openSubmenu === item.id ? null : item.id);
    } else {
      navigate(item.path);
      if (onMobileClose) {
        onMobileClose();
      }
    }
  };

  const renderMenuItem = (item) => {
    const isSelected = !item.submenu && getCurrentMenuItem() === item.id;
    const hasSubmenu = !!item.submenu;

    return (
      <React.Fragment key={item.id}>
        <ListItem disablePadding>
          <ListItemButton
            selected={isSelected}
            onClick={() => handleMenuClick(item)}
            data-id={item.id}
            className={item.id === 'dashboard' ? 'dashboard-link' : 
                      item.id === 'resources' ? 'resources-section' :
                      item.id === 'articles' ? 'articles-section' :
                      item.id === 'settings' ? 'settings-section' : ''}
            sx={{
              '&.Mui-selected': {
                backgroundColor: 'primary.main',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
              },
              color: 'text.primary',
            }}
          >
            <Box sx={{ mr: 2, color: 'inherit' }}>{item.icon}</Box>
            <ListItemText primary={item.label} />
            {hasSubmenu && (
              openSubmenu === item.id ? <ExpandLess /> : <ExpandMore />
            )}
          </ListItemButton>
        </ListItem>
        {hasSubmenu && (
          <Collapse in={openSubmenu === item.id} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.submenu.map((subItem) => (
                <ListItem key={subItem.id} disablePadding>
                  <ListItemButton
                    selected={getCurrentMenuItem() === subItem.id}
                    onClick={() => {
                      navigate(subItem.path);
                      if (onMobileClose) {
                        onMobileClose();
                      }
                    }}
                    data-id={subItem.id}
                    sx={{
                      pl: 4,
                      color: 'text.primary',
                      '&.Mui-selected': {
                        backgroundColor: 'primary.main',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: 'primary.dark',
                        },
                      },
                    }}
                  >
                    <ListItemIcon sx={{ 
                      minWidth: 40,
                      color: 'inherit'
                    }}>
                      {subItem.icon}
                    </ListItemIcon>
                    <ListItemText primary={subItem.label} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  const handleCreateProject = async () => {
    try {
      await createProject({
        name: newProjectName,
        description: newProjectDescription,
      });
      setCreateDialogOpen(false);
      setNewProjectName('');
      setNewProjectDescription('');
      await reloadProjects();
    } catch (error) {
      console.error('Error creating project:', error);
    }
  };

  const drawer = (
    <>
      <Box sx={{ p: 2 }}>
        <FormControl fullWidth>
          <Select
            value={selectedProject?.id || ''}
            onChange={(e) => {
              if (e.target.value === '') {
                setSelectedProject(null);
              } else {
                const project = projects.find(p => p.id === e.target.value);
                setSelectedProject(project || null);
              }
            }}
            displayEmpty
            sx={{ mb: 1 }}
            className="project-selector"
          >
            {projects.map((project) => (
              <MenuItem key={project.id} value={project.id}>
                {project.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
          variant="outlined"
          fullWidth
          size="small"
        >
          New Project
        </Button>
      </Box>
      
      <Divider />

      <List>
        {MENU_ITEMS.map(renderMenuItem)}
      </List>
    </>
  );

  return (
    <>
      {/* Desktop drawer */}
      {!mobileOpen && (
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            width: DRAWER_WIDTH,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: DRAWER_WIDTH,
              boxSizing: 'border-box',
              top: 64,
              height: 'calc(100% - 64px)',
              backgroundColor: 'background.paper',
            },
          }}
        >
          {drawer}
        </Drawer>
      )}

      {/* Mobile content */}
      {mobileOpen && drawer}

      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)}>
        <DialogTitle>Create New Project</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Project Name"
            fullWidth
            value={newProjectName}
            onChange={(e) => setNewProjectName(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            rows={3}
            value={newProjectDescription}
            onChange={(e) => setNewProjectDescription(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleCreateProject}
            variant="contained"
            disabled={!newProjectName.trim()}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ProjectSidebar; 